import { Flex, List, Radio, Stack, Text, Tooltip } from '@mantine/core';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectInput } from '../../../shared/components/Inputs/Inputs';
import { ActivityType } from '../../../shared/types';
import {
  countryOptions,
  EU_COUNTRY_LIST,
} from '../../../../assets/countryList';
import classes from './TransactionForm.module.css';
import { BetaIcon } from '../../../../lib/components';

type ActivityTypeInputProps = {
  disabled: boolean;
};

const COUNTRY_ACTIVITY_LABELS: Record<ActivityType, string> = {
  IMPORT: 'transactions.form.countryOfEntry',
  EXPORT: 'transactions.form.countryOfExit',
  DOMESTIC: 'transactions.form.countryOfActivity',
  TRADE: 'transactions.form.countryOfActivity',
};

const ActivityTypeInput = ({ disabled }: ActivityTypeInputProps) => {
  const { t } = useTranslation();
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const selectedActivity = watch('activityType') as ActivityType;
  const hasDds = !!watch('importedDds')?.length;

  const activityTypes: ActivityType[] = [
    ActivityType.IMPORT,
    ActivityType.EXPORT,
    ActivityType.DOMESTIC,
    ActivityType.TRADE,
  ];

  return (
    <Controller
      name="activityType"
      rules={{ required: true }}
      control={control}
      render={({ field }) => (
        <Radio.Group
          mt="lg"
          label={
            <Tooltip
              position="right"
              label={
                <List>
                  {activityTypes.map((activity) => (
                    <List.Item key={activity}>
                      <Flex gap="4px" align="center">
                        <Text fw={600}>
                          {t(`transactions.activity.${activity}`)}:
                        </Text>
                        <Text>
                          {t(`transactions.activityTypeTooltip.${activity}`)}
                        </Text>
                      </Flex>
                    </List.Item>
                  ))}
                </List>
              }
            >
              <Text size="sm" display="flex" style={{ gap: '4px' }}>
                {t('transactions.form.activity')}
                <BetaIcon icon="circle-info" size="sm" />
              </Text>
            </Tooltip>
          }
          {...field}
          aria-disabled={disabled}
        >
          <Stack mt="xs">
            {activityTypes.map((activity) => {
              const disableTrade = activity === ActivityType.TRADE && !hasDds;
              return (
                <div key={activity}>
                  <Radio
                    value={activity}
                    classNames={{
                      label: classes.activityTypeLabel,
                    }}
                    label={
                      <Tooltip
                        disabled={!disableTrade}
                        label={t('transactions.form.tradeHint')}
                      >
                        <Flex gap="4px" align="center">
                          <Text>{t(`transactions.activity.${activity}`)}</Text>
                          <Text component="span">
                            {t(`transactions.activityTypeHint.${activity}`)}
                          </Text>
                        </Flex>
                      </Tooltip>
                    }
                    error={errors.activityType?.message?.toString()}
                    disabled={disabled || disableTrade}
                  />
                  {selectedActivity === activity && (
                    <SelectInput
                      mt="md"
                      name="countryOfActivity"
                      label={t(COUNTRY_ACTIVITY_LABELS[selectedActivity])}
                      data={countryOptions(EU_COUNTRY_LIST)}
                      disabled={disabled}
                    />
                  )}
                </div>
              );
            })}
          </Stack>
        </Radio.Group>
      )}
    />
  );
};

export default ActivityTypeInput;
